import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, filter, map, Observable, of, switchMap } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { GetFilteredValuesResponse } from '@blaze-shared/api-contracts-domain';
import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { DatasetsApiService } from '@blaze-shared/datasets-data-access';
import { DataBindingSourceInfo } from '@blaze-shared/elements-domain';
import { compareAscBy, compareByNested } from '@blaze-shared/platform-util';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeSchema,
  convertDatasetColumnsToColumnInfos,
  createArrayOfObjectsSchemaFromColumns,
  createObjectSchemaFromColumns,
  DatasetColumn,
  findColumn,
  SCHEMA_COLUMNS_LINK,
} from '@blaze-shared/schema-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';
import {
  findFirstNodeInWorkflowOfType,
  isGetNode,
  WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { DataProviderQuery, InputDataPackage } from '../../io-data.models';

import { DataSourceProvider } from './data-source.data-provider';
import { createTagItemsSchema } from './utils/schema.utils';

const VALUE_COLUMN_DESCRIPTOR = {
  columnType: 'String' as const,
  dataType: 'String' as const,
  isProfilePicture: false,
  name: 'value',
};

@Injectable({ providedIn: 'root' })
export class ColumnFilterDataProvider implements DataSourceProvider {
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  readonly sourceType = 'column-filter' as const;

  getDataStream({ source }: Pick<DataProviderQuery, 'source'>): Observable<InputDataPackage> {
    return this.getWorkflowWithSchema(source.id).pipe(
      filter((payload): payload is NonNullable<typeof payload> => payload != null),
      distinctUntilChanged(isDeepEqual),
      switchMap((payload) => this.getFilterDataAndSchema(payload, source)),
      filter(({ schema }) => !!schema),
      map(({ data, schema, column }) => ({
        sourceType: 'column-filter' as const,
        data: data.values,
        schema,
        column,
      })),
      distinctUntilChanged(isDeepEqual),
    );
  }

  /** BL-9575: re-fetch when workflow or preview user changes */
  private getWorkflowWithSchema(workflowId: string): Observable<
    | {
        datasetSchema: BlazeSchema;
        firstGetNode: WorkflowNode | undefined;
      }
    | undefined
  > {
    return combineLatest({
      varUser: this.projectContextFacade.varUser$.pipe(
        distinctUntilChanged((prev, curr) => prev?.Email === curr?.Email),
      ),
      workflow: this.store.select(runtimeSelectors.selectWorkflowById(workflowId)),
    }).pipe(
      map(({ workflow }) => workflow),
      filter((workflow) => !!workflow),
      switchMap((workflow) => {
        const firstGetNode = findFirstNodeInWorkflowOfType(workflow, isGetNode);
        if (!firstGetNode?.datasetId) {
          return of(undefined);
        }
        return this.schemaService.obtainSchema(firstGetNode.datasetId).pipe(
          filter((s): s is BlazeSchema => s !== null),
          map((datasetSchema) => ({ datasetSchema, firstGetNode })),
        );
      }),
    );
  }

  private getFilterDataAndSchema(
    payload: { datasetSchema: BlazeSchema; firstGetNode: WorkflowNode | undefined },
    source: DataBindingSourceInfo,
  ): Observable<{
    data: GetFilteredValuesResponse;
    schema: BlazeSchema;
    column: DatasetColumn | undefined;
  }> {
    const { datasetSchema, firstGetNode } = payload;

    if (!datasetSchema?.id) {
      throw new Error('Dataset source schema for filters is missing');
    }
    if (!source.name) {
      throw new Error('Dataset source.name is missing');
    }

    const column = findColumn(datasetSchema.columns ?? [], source.name);

    if (column?.columnType === 'Boolean') {
      return of({
        data: { values: [{ value: true }, { value: false }] },
        schema: createBooleanFilterSchema(column.name),
        column,
      });
    }

    if (column?.columnType === 'Tag' && column.tagDefinition) {
      return of({
        data: { values: column.tagDefinition.items },
        schema: createTagItemsSchema(column),
        column,
      });
    }

    const getNode = isGetNode(firstGetNode) ? firstGetNode : null;
    return this.datasetsApiService
      .postFilteredValues(datasetSchema.id, {
        column: source.name,
        query: getNode?.query,
        queryConditions: getNode?.queryConditions,
      })
      .pipe(
        map((filterValues) => ({
          ...processApiFilterResponse(filterValues, column),
          column,
        })),
      );
  }
}

function createBooleanFilterSchema(columnName: string): BlazeSchema {
  return createObjectSchemaFromColumns(columnName, [
    {
      columnType: 'Boolean',
      dataType: 'Boolean',
      isProfilePicture: false,
      name: 'value',
    },
  ]);
}

function createSimpleValuesSchema(): BlazeSchema {
  return createArrayOfObjectsSchemaFromColumns('value', [VALUE_COLUMN_DESCRIPTOR]);
}

function createLinkValuesSchema(columnName: string): BlazeSchema {
  return createObjectSchemaFromColumns(columnName, [
    {
      columnType: 'Object',
      dataType: 'Object',
      isProfilePicture: false,
      name: 'value',
      dataTypeDef: {
        name: 'Link',
        columns: convertDatasetColumnsToColumnInfos(SCHEMA_COLUMNS_LINK),
      },
    },
  ]);
}

function processApiFilterResponse(
  response: GetFilteredValuesResponse,
  column: DatasetColumn | undefined,
): { data: GetFilteredValuesResponse; schema: BlazeSchema } {
  const hasValues = response.values.length > 0;
  const isSimpleValue = hasValues && ['string', 'number'].includes(typeof response.values[0].value);

  if (isSimpleValue) {
    const sorted = response.values.toSorted(compareAscBy('value'));
    return {
      data: { ...response, values: sorted },
      schema: createSimpleValuesSchema(),
    };
  }

  const sorted = response.values.toSorted(compareByNested('value', 'display'));
  return {
    data: { ...response, values: sorted },
    schema: createLinkValuesSchema(column?.name ?? 'LinkValue'),
  };
}
